import { useState, useCallback, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}

export default function TagInput({
  tags,
  onChange,
  placeholder,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");

  const addTag = useCallback(
    (raw: string) => {
      const tag = raw.trim();
      if (!tag) return;
      const isDuplicate = tags.some(
        (t) => t.toLowerCase() === tag.toLowerCase(),
      );
      if (isDuplicate) return;
      onChange([...tags, tag]);
      setInputValue("");
    },
    [tags, onChange],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(tags.filter((_, i) => i !== index));
    },
    [tags, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        addTag(inputValue);
      }
    },
    [inputValue, addTag],
  );

  const handleChange = useCallback(
    (value: string) => {
      if (value.includes(",")) {
        const parts = value.split(",");
        for (const part of parts.slice(0, -1)) {
          addTag(part);
        }
        setInputValue(parts[parts.length - 1]);
      } else {
        setInputValue(value);
      }
    },
    [addTag],
  );

  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-line text-fg text-meta"
        >
          {tag}
          <button
            type="button"
            onClick={() => removeTag(i)}
            className="hover:text-fg"
            aria-label={`Remove ${tag}`}
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <Input
        className="h-7 text-meta flex-1 min-w-[100px]"
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
    </div>
  );
}
